import { Component, EventEmitter, Output, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogComponent } from 'src/app/@core/components/ui/dialog/dialog.component';
import { Subscription } from 'rxjs';
import { ScreenRecordingService } from '../../../@shared/services/screen-recording.service';
import { ToastService } from '../../../@core/services/toast.service';
import { SVG } from '../../../common/svgs';
import { SanitizeHtmlPipe } from '../../pipes/sanitizeHtml.pipe';
import { LottieAnimationComponent } from '../lottie-animation/lottie-animation.component';
import { CaptureResult } from '../../../@core/models/model';

@Component({
    selector: 'app-capture-issue',
    standalone: true,
    imports: [CommonModule, DialogComponent, SanitizeHtmlPipe, LottieAnimationComponent],
    templateUrl: './capture-issue.component.html',
    styleUrl: './capture-issue.component.scss'
})
export class CaptureIssueComponent implements OnInit, OnDestroy {
    @Output() captureCancelled = new EventEmitter<void>();
    @Output() captureCompleted = new EventEmitter<CaptureResult>();

    captureDialogVisible: boolean = false;
    capturingVideoLoadingDialogVisible: boolean = false;
    capturingScreenshotLoadingDialogVisible: boolean = false;
    isRecording: boolean = false;
    recordingTime: number = 0;
    lottieAnimationData: any = null;
    SVG = SVG;

    private recordingStateSubscription?: Subscription;
    private recordingCancelledSubscription?: Subscription;
    private recordingCompletedSubscription?: Subscription;

    constructor(
        private screenRecordingService: ScreenRecordingService,
        private toastService: ToastService
    ) { }

    ngOnInit(): void {
        this.loadLottieAnimation();

        this.recordingStateSubscription = this.screenRecordingService.recordingState$.subscribe(state => {
            this.isRecording = state.isRecording;
            this.recordingTime = state.recordingTime;
        });

        this.recordingCancelledSubscription = this.screenRecordingService.recordingCancelled$.subscribe(() => {
            this.capturingVideoLoadingDialogVisible = false;
            this.capturingScreenshotLoadingDialogVisible = false;
            this.toastService.error('Video recording cancelled.');
            this.captureCancelled.emit();
        });

        this.recordingCompletedSubscription = this.screenRecordingService.recordingCompleted$.subscribe(({ file, videoUrl }) => {
            this.capturingVideoLoadingDialogVisible = true;
            this.toastService.success('Video captured successfully.');
            this.captureCompleted.emit({
                file,
                preview: videoUrl,
                isImage: false,
                isVideo: true
            });
            this.capturingVideoLoadingDialogVisible = false;
        });
    }

    ngOnDestroy() {
        this.recordingStateSubscription?.unsubscribe();
        this.recordingCancelledSubscription?.unsubscribe();
        this.recordingCompletedSubscription?.unsubscribe();
    }

    openCaptureDialog() {
        this.captureDialogVisible = true;
    }

    onCaptureDialogHide() {
        this.captureDialogVisible = false;
    }

    async captureScreenshot() {
        this.captureDialogVisible = false;

        try {
            const stream = await navigator.mediaDevices.getDisplayMedia({
                video: {
                    displaySurface: 'monitor',
                    width: { ideal: 1920 },
                    height: { ideal: 1080 }
                },
                audio: false,
                preferCurrentTab: false
            } as any);

            const video = document.createElement('video');
            video.srcObject = stream;
            video.autoplay = true;
            video.playsInline = true;
            video.muted = true;

            await new Promise<void>((resolve, reject) => {
                video.onloadedmetadata = () => {
                    video.play().then(() => resolve()).catch(reject);
                };
                setTimeout(() => reject(new Error('Video loading timeout')), 5000);
            });

            await new Promise(resolve => setTimeout(resolve, 300));

            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext('2d');

            if (!ctx) {
                throw new Error('Failed to get canvas context');
            }

            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            stream.getTracks().forEach(track => track.stop());
            video.srcObject = null;

            this.capturingScreenshotLoadingDialogVisible = true;

            const dataUrl = canvas.toDataURL('image/png', 1.0);
            const filename = `screenshot-${Date.now()}.png`;
            const file = this.dataURLtoFile(dataUrl, filename);

            this.capturingScreenshotLoadingDialogVisible = false;

            this.toastService.success('Screenshot captured successfully.');
            this.captureCompleted.emit({
                file,
                preview: dataUrl,
                isImage: true,
                isVideo: false
            });
        } catch (error) {
            this.toastService.error('Failed to capture screenshot. Please try again.');
            this.captureCancelled.emit();
        }
    }

    async captureVideo() {
        this.captureDialogVisible = false;
        await new Promise(r => setTimeout(r, 200));
        this.startRecording();
    }

    async startRecording() {
        try {
            await this.screenRecordingService.startRecording();
        } catch (error) {
            this.toastService.error('Could not record video. Please try again.');
            this.captureCancelled.emit();
        }
    }

    stopRecording() {
        this.screenRecordingService.stopRecording();
    }

    formatTime(seconds: number) {
        return this.screenRecordingService.formatTime(seconds);
    }

    loadLottieAnimation() {
        fetch('/animations/screenshot_loading.json')
            .then(response => response.json())
            .then(data => this.lottieAnimationData = data)
            .catch(() => { });
    }

    private dataURLtoFile(dataUrl: string, filename: string): File {
        const arr = dataUrl.split(',');
        const mime = arr[0].match(/:(.*?);/)![1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) u8arr[n] = bstr.charCodeAt(n);
        return new File([u8arr], filename, { type: mime });
    }
}
